import React, { useEffect, useRef } from 'react';
import { bool, func, object, string } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { HiSparkles } from 'react-icons/hi2';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { EditProjectListingDraftForm } from '../../forms';
import NamedLink from '../NamedLink/NamedLink';
import { MANUAL_FILL_FALLBACK_TITLE } from '../EditProjectListingChatPanel/EditProjectListingChatPanel';
import {
  generateDraft,
  requestSaveListingDraft,
} from '../../containers/EditProjectListingPage/EditProjectListingPage.duck';

import css from './EditProjectListingDraftPanel.css';

// Map the form values (title + description + the three budget selects) to a
// listing update payload merged over the listing's existing publicData. Shared
// by publish (onSubmit) and save-as-draft. A blank title is omitted rather than
// sent as "": Sharetribe rejects an empty title on own_listings/update, which
// made "Save draft" fail silently in the manual-fill flow (the seeded placeholder
// title is shown as empty there). Omitting it keeps the listing's current title.
export const buildUpdatePayload = (values, publicData) => {
  const title = (values.title || '').trim();
  return {
    ...(title ? { title } : {}),
    publicData: {
      ...(publicData || {}),
      descriptionText: values.descriptionText || '',
      projectDuration: values.projectDuration,
      projectUrgency: values.projectUrgency,
      priceEstimate: values.priceEstimate,
    },
  };
};

const EditProjectListingDraftPanelComponent = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    saveDraftActionMsg,
    saveDraftInProgress,
    onSaveDraft,
    panelUpdated,
    updateInProgress,
    errors,
    intl,
    draft,
    onGenerate,
    onPersistDraft,
    aiChatLinkParams,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { title, publicData, privateData, state } = currentListing.attributes;
  const isPublished = state === LISTING_STATE_PUBLISHED;
  const startedRef = useRef(false);
  const persistedRef = useRef(false);

  // Pull the writer's context from the listing's persisted privateData/publicData.
  // This is the source of truth across tab transitions and reloads — Redux can be
  // stale because SHOW_PROJECTS_SUCCESS resets the page slice on each loadData.
  const transcript =
    (privateData && privateData.jobPostingChat && privateData.jobPostingChat.transcript) || [];
  const manualFill = !!(
    privateData &&
    privateData.jobPostingChat &&
    privateData.jobPostingChat.manualFill
  );
  const budget = {
    priceEstimate: publicData && publicData.priceEstimate,
    projectDuration: publicData && publicData.projectDuration,
    projectUrgency: publicData && publicData.projectUrgency,
  };
  const hasContext = transcript.length > 0;

  // Auto-kick draft generation on first mount when we have a transcript and no
  // existing draft. Skip auto-generation when the user explicitly chose "fill
  // the form yourself" on the chat panel — they want a blank editor.
  useEffect(() => {
    const hasExisting = !!(title && publicData && publicData.descriptionText);
    if (
      !startedRef.current &&
      hasContext &&
      !manualFill &&
      !draft.fields &&
      !draft.isStreaming &&
      !hasExisting
    ) {
      startedRef.current = true;
      onGenerate({ transcript, budget });
    }
  }, [
    draft.fields,
    draft.isStreaming,
    onGenerate,
    title,
    publicData,
    hasContext,
    manualFill,
    transcript,
    budget,
  ]);

  // Persist the writer's first draft to the listing the moment it finishes
  // streaming. Without this, the generated title/description live only in Redux,
  // so a page refresh (which resets the page slice) loses them and the writer is
  // re-triggered. Saving descriptionText makes both re-generation guards
  // (loadData's validDraftContext and the auto-kick above) skip on re-entry.
  useEffect(() => {
    const alreadyPersisted = !!(publicData && publicData.descriptionText);
    if (
      persistedRef.current ||
      manualFill ||
      draft.isStreaming ||
      !draft.fields ||
      !draft.fields.descriptionText ||
      alreadyPersisted ||
      !currentListing.id
    ) {
      return;
    }
    persistedRef.current = true;
    // Snapshot the writer's untouched output into privateData.agentListingDraft
    // on this first persist. This is the agent's original draft, frozen for later
    // comparison against whatever the user ends up publishing — so we write it
    // exactly once and never overwrite an existing snapshot.
    const hasAgentDraftSnapshot = !!(privateData && privateData.agentListingDraft);
    onPersistDraft({
      id: currentListing.id,
      title: (draft.fields.title || title || '').trim(),
      publicData: {
        ...(publicData || {}),
        descriptionText: draft.fields.descriptionText || '',
        projectDuration: budget.projectDuration,
        projectUrgency: budget.projectUrgency,
        priceEstimate: budget.priceEstimate,
      },
      ...(hasAgentDraftSnapshot
        ? {}
        : { privateData: { ...(privateData || {}), agentListingDraft: draft.fields } }),
    });
  }, [
    draft.fields,
    draft.isStreaming,
    manualFill,
    publicData,
    privateData,
    title,
    budget,
    currentListing.id,
    onPersistDraft,
  ]);

  // Seed the editable budget/scope selections from the listing's publicData.
  const budgetInitialValues = {
    projectDuration: (publicData && publicData.projectDuration) || undefined,
    projectUrgency: (publicData && publicData.projectUrgency) || undefined,
    priceEstimate: (publicData && publicData.priceEstimate) || undefined,
  };

  // The manual-fill flow seeds the listing with a placeholder title so the draft
  // can be created. Treat that placeholder as empty in the form so the title
  // field shows its placeholder text instead of the seed.
  const displayTitle = title === MANUAL_FILL_FALLBACK_TITLE ? '' : title;

  const initialValues = draft.fields
    ? {
        title: draft.fields.title || displayTitle || '',
        descriptionText:
          draft.fields.descriptionText || (publicData && publicData.descriptionText) || '',
        ...budgetInitialValues,
      }
    : {
        title: displayTitle || '',
        descriptionText: (publicData && publicData.descriptionText) || '',
        ...budgetInitialValues,
      };

  // Heading copy depends on context: editing a published listing, filling the
  // form manually (no AI draft to review, so the "review" copy doesn't fit), or
  // reviewing an AI-generated draft.
  const titleId = isPublished
    ? 'EditProjectListingDraftPanel.titlePublished'
    : manualFill
    ? 'EditProjectListingDraftPanel.titleManual'
    : 'EditProjectListingDraftPanel.title';
  const subtitleId = isPublished
    ? 'EditProjectListingDraftPanel.subtitlePublished'
    : manualFill
    ? 'EditProjectListingDraftPanel.subtitleManual'
    : 'EditProjectListingDraftPanel.subtitle';

  // A user who chose to fill the form manually can still switch to the AI chat.
  // The listing stays a manual-fill draft until the first chat turn is saved.
  const showAiChatLink = manualFill && !isPublished && !!aiChatLinkParams;

  return (
    <div className={classes}>
      <div className={css.inner}>
        <div className={css.header}>
          <div className={css.headerText}>
            <h1 className={css.title}>
              <FormattedMessage id={titleId} />
            </h1>
            <p className={css.subtitle}>
              <FormattedMessage id={subtitleId} />
            </p>
          </div>
          {showAiChatLink ? (
            <NamedLink
              className={css.aiChatLink}
              name="EditProjectListingPage"
              params={aiChatLinkParams}
            >
              <HiSparkles className={css.aiChatLinkIcon} aria-hidden="true" />
              <FormattedMessage id="EditProjectListingDraftPanel.aiChatLink" />
            </NamedLink>
          ) : null}
        </div>

        {draft.error && (
          <div className={css.error}>
            {draft.error.message ||
              intl.formatMessage({ id: 'EditProjectListingDraftPanel.generationFailed' })}
          </div>
        )}

        {!hasContext && !manualFill && !draft.fields && !draft.isStreaming && (
          <div className={css.error}>
            <FormattedMessage id="EditProjectListingDraftPanel.missingTranscript" />
          </div>
        )}

        <EditProjectListingDraftForm
          key={draft.fields ? 'drafted' : 'empty'}
          className={css.form}
          initialValues={initialValues}
          isGenerating={draft.isStreaming}
          saveActionMsg={submitButtonText}
          saveDraftActionMsg={saveDraftActionMsg}
          saveDraftInProgress={saveDraftInProgress}
          onSaveDraft={
            onSaveDraft ? values => onSaveDraft(buildUpdatePayload(values, publicData)) : null
          }
          onSubmit={values => {
            onSubmit(buildUpdatePayload(values, publicData));
          }}
          onChange={onChange}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}
        />
      </div>
    </div>
  );
};

EditProjectListingDraftPanelComponent.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
  onSaveDraft: null,
  saveDraftActionMsg: null,
  saveDraftInProgress: false,
  aiChatLinkParams: null,
};

EditProjectListingDraftPanelComponent.propTypes = {
  className: string,
  rootClassName: string,
  listing: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  saveDraftActionMsg: string,
  saveDraftInProgress: bool,
  onSaveDraft: func,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object,
  intl: intlShape.isRequired,
  draft: object.isRequired,
  onGenerate: func.isRequired,
  onPersistDraft: func.isRequired,
  // Route params of the wizard's chat tab; given only in the new-listing flow
  aiChatLinkParams: object,
};

const mapStateToProps = state => ({
  draft: state.EditProjectListingPage.jobPostingDraft,
});

const mapDispatchToProps = dispatch => ({
  onGenerate: payload => dispatch(generateDraft(payload)),
  onPersistDraft: payload => dispatch(requestSaveListingDraft(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(EditProjectListingDraftPanelComponent));
